import React from 'react';

const HomeworkManagement = ({ homeworkAssignments, selectedDate }) => {
  const getPriorityColor = (priority) => {
    const colors = {
      'high': 'bg-red-100 text-red-800 border-red-200',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'low': 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'high': '🔴',
      'medium': '🟡',
      'low': '🟢'
    };
    return icons[priority] || '⚪';
  };

  const getPriorityText = (priority) => {
    const texts = {
      'high': '高優先級',
      'medium': '中優先級',
      'low': '低優先級'
    };
    return texts[priority] || '未知優先級';
  };

  const getStatusColor = (status) => {
    const colors = {
      'completed': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'overdue': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      'completed': '已完成',
      'pending': '待完成',
      'overdue': '已逾期'
    };
    return texts[status] || '未知狀態';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}年${month}月${day}日`;
  };

  const isOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    return due < today;
  };

  return (
    <div className="homework-management">
      <div className="homework-header">
        <h3>家課管理</h3>
        <p>顯示指定日期的所有家課</p>
      </div>

      {homeworkAssignments.length === 0 ? (
        <div className="empty-homework">
          <div className="empty-icon">📝</div>
          <h4>當日無家課</h4>
          <p>選定的日期沒有安排家課</p>
        </div>
      ) : (
        <div className="homework-list">
          {homeworkAssignments.map((homework, index) => {
            const overdue = isOverdue(homework.due_date);
            const status = overdue && homework.completion_status === 'pending' ? 'overdue' : homework.completion_status;
            
            return (
              <div key={homework.id} className="homework-item">
                <div className="homework-priority">
                  <span className="priority-icon">{getPriorityIcon(homework.priority_level)}</span>
                  <span className={`priority-badge ${getPriorityColor(homework.priority_level)}`}>
                    {getPriorityText(homework.priority_level)}
                  </span>
                </div>

                <div className="homework-content">
                  <div className="homework-header-info">
                    <h4 className="homework-title">{homework.assignment_title}</h4>
                    <span className={`status-badge ${getStatusColor(status)}`}>
                      {getStatusText(status)}
                    </span>
                  </div>

                  <div className="homework-details">
                    <div className="detail-row">
                      <span className="detail-label">科目:</span>
                      <span className="detail-value">{homework.subject}</span>
                    </div>
                    
                    <div className="detail-row">
                      <span className="detail-label">作業描述:</span>
                      <span className="detail-value">{homework.assignment_description}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">指派日期:</span>
                      <span className="detail-value">{formatDate(homework.assigned_date)}</span>
                    </div>

                    <div className="detail-row">
                      <span className="detail-label">截止日期:</span>
                      <span className={`detail-value ${overdue ? 'overdue-text' : ''}`}>
                        {formatDate(homework.due_date)}
                        {overdue && <span className="overdue-indicator"> (已逾期)</span>}
                      </span>
                    </div>

                    {homework.related_course_id && (
                      <div className="detail-row">
                        <span className="detail-label">相關課程:</span>
                        <span className="detail-value">課程 ID: {homework.related_course_id}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="homework-summary">
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-number">{homeworkAssignments.length}</span>
            <span className="stat-label">總家課數</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {homeworkAssignments.filter(h => h.completion_status === 'completed').length}
            </span>
            <span className="stat-label">已完成</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {homeworkAssignments.filter(h => h.completion_status === 'pending').length}
            </span>
            <span className="stat-label">待完成</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">
              {homeworkAssignments.filter(h => isOverdue(h.due_date) && h.completion_status === 'pending').length}
            </span>
            <span className="stat-label">已逾期</span>
          </div>
        </div>

        <div className="priority-breakdown">
          <h4>優先級分布</h4>
          <div className="priority-stats">
            <div className="priority-stat">
              <span className="priority-icon">🔴</span>
              <span className="priority-count">
                {homeworkAssignments.filter(h => h.priority_level === 'high').length}
              </span>
              <span className="priority-label">高優先級</span>
            </div>
            <div className="priority-stat">
              <span className="priority-icon">🟡</span>
              <span className="priority-count">
                {homeworkAssignments.filter(h => h.priority_level === 'medium').length}
              </span>
              <span className="priority-label">中優先級</span>
            </div>
            <div className="priority-stat">
              <span className="priority-icon">🟢</span>
              <span className="priority-count">
                {homeworkAssignments.filter(h => h.priority_level === 'low').length}
              </span>
              <span className="priority-label">低優先級</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeworkManagement;
